import React, { useState, useEffect, useCallback } from 'react';
import {
    DollarSign, TrendingUp, CreditCard, Users, Target,
    Loader2, AlertTriangle, Award, BarChart2, Zap,
} from 'lucide-react';
import { adminAnalytics, internalAnalytics } from '../../services/api';
import MetricCard from './MetricCard';
import { SectionHeader, Section, StatusBadge, EmptyMetric } from './OverviewTabV2';

/**
 * MonetizationTabV2 — "Where is upgrade / activation performance improving or leaking?"
 *
 * Renders "Not yet instrumented" honestly for revenue/ARPU.
 */

const MonetizationTabV2 = ({ filters = {} }) => {
    const [monetization, setMonetization] = useState(null);
    const [legacyMetrics, setLegacyMetrics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const loadData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const [monRes, legRes] = await Promise.allSettled([
                adminAnalytics.getMonetization({ days: filters.days || 7 }),
                internalAnalytics.getMetrics(),
            ]);

            if (monRes.status === 'fulfilled') setMonetization(monRes.value.data);
            if (legRes.status === 'fulfilled') setLegacyMetrics(legRes.value.data);
        } catch (err) {
            setError(err.message || 'Failed to load monetization data.');
        } finally {
            setLoading(false);
        }
    }, [filters.days]);

    useEffect(() => { loadData(); }, [loadData]);

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, flexDirection: 'column', gap: 10 }}>
                <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: '#6366f1' }} />
                <span style={{ fontSize: 13, color: '#6b7280' }}>Loading monetization…</span>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, flexDirection: 'column', gap: 10 }}>
                <AlertTriangle size={24} style={{ color: '#ef4444' }} />
                <p style={{ fontSize: 14, color: '#ef4444', fontWeight: 500, margin: 0 }}>{error}</p>
                <button onClick={loadData} style={{ fontSize: 13, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer' }}>Retry</button>
            </div>
        );
    }

    const upgrade = monetization?.upgrade_funnel || {};
    const activation = monetization?.activation_funnel || {};
    const attribution = monetization?.attribution || {};
    const experiments = monetization?.experiments || {};
    const retention = monetization?.retention || {};
    const winners = monetization?.winner_signals || {};

    // Legacy metrics fallback
    const legacy = legacyMetrics?.monetization || {};
    const creditsFromLegacy = legacy.avatar_credits_purchased_last_7_days;
    const conversionFromLegacy = legacy.avatar_conversion_rate;

    // Revenue / ARPU — always not instrumented unless backend explicitly provides
    const revenue = legacy.revenue;
    const arpu = legacy.arpu;
    const revenueNote = legacy.revenue_note;

    return (
        <div>
            <SectionHeader
                title="Monetization"
                subtitle="Where is upgrade / activation performance improving or leaking?"
            />

            {/* ── Key Numbers ────────────────────────────────────── */}
            <Section title="Key Metrics" icon={DollarSign} color="#10b981">
                <MetricCard
                    label="Credits Purchased"
                    value={creditsFromLegacy ?? '—'}
                    icon={CreditCard} accent="#10b981"
                    sub="Last 7 days"
                />
                <MetricCard
                    label="Avatar Conversion Rate"
                    value={conversionFromLegacy ?? '—'}
                    icon={Users} accent="#3b82f6"
                    format="percent"
                />
                {attribution.top_source ? (
                    <MetricCard
                        label="Top Attribution Source"
                        value={attribution.top_source}
                        icon={Award} accent="#8b5cf6"
                        sub={attribution.source_count ? `${attribution.source_count} total sources` : null}
                    />
                ) : (
                    <EmptyMetric label="Top Attribution Source" reason="no_data" />
                )}
                {/* Revenue — not yet instrumented */}
                {revenue != null ? (
                    <MetricCard label="Revenue" value={`$${revenue}`} icon={DollarSign} accent="#10b981" />
                ) : (
                    <EmptyMetric label="Revenue" reason="not_instrumented" />
                )}
                {arpu != null ? (
                    <MetricCard label="ARPU" value={`$${arpu}`} icon={TrendingUp} accent="#3b82f6" />
                ) : (
                    <EmptyMetric label="ARPU" reason="not_instrumented" />
                )}
            </Section>

            {/* ── Upgrade Funnel ──────────────────────────────────── */}
            <FunnelDisplay title="Upgrade Funnel" data={upgrade} color="#10b981" />

            {/* ── Activation Funnel ───────────────────────────────── */}
            <FunnelDisplay title="Activation Funnel" data={activation} color="#6366f1" />

            {/* ── Winner Signals ──────────────────────────────────── */}
            {Object.keys(winners).length > 0 && (
                <div style={{ marginBottom: 28 }}>
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        marginBottom: 12, paddingBottom: 8,
                        borderBottom: '2px solid #f59e0b20',
                    }}>
                        <Award size={15} style={{ color: '#f59e0b' }} />
                        <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: 0 }}>Experiment Signals</h3>
                    </div>
                    <div style={{
                        background: '#fff', border: '1px solid #e5e7eb',
                        borderRadius: 14, padding: '16px 20px',
                    }}>
                        {Object.entries(winners).map(([key, signal], idx) => (
                            <div key={key} style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '8px 0',
                                borderBottom: idx < Object.keys(winners).length - 1 ? '1px solid #f1f5f9' : 'none',
                            }}>
                                <span style={{ fontSize: 12, color: '#374151', fontWeight: 500 }}>
                                    {key.replace(/_/g, ' ')}
                                </span>
                                <span style={{
                                    fontSize: 12, fontWeight: 600,
                                    color: signal === 'winner' ? '#10b981' : signal === 'loser' ? '#ef4444' : '#6b7280',
                                }}>
                                    {typeof signal === 'string' ? signal : JSON.stringify(signal)}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Retention / Cohort ─────────────────────────────── */}
            {retention.cohorts && retention.cohorts.length > 0 && (
                <div style={{ marginBottom: 28 }}>
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        marginBottom: 12, paddingBottom: 8,
                        borderBottom: '2px solid #3b82f620',
                    }}>
                        <BarChart2 size={15} style={{ color: '#3b82f6' }} />
                        <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: 0 }}>Monetization Retention</h3>
                    </div>
                    <div style={{
                        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
                        overflow: 'hidden',
                    }}>
                        {retention.cohorts.slice(0, 8).map((c, i) => (
                            <div key={i} style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '10px 16px',
                                borderBottom: i < Math.min(retention.cohorts.length, 8) - 1 ? '1px solid #f1f5f9' : 'none',
                            }}>
                                <span style={{ fontSize: 12, color: '#374151' }}>{c.cohort || c.label || `Cohort ${i + 1}`}</span>
                                <span style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>
                                    {c.rate != null ? `${c.rate}%` : '—'}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {revenueNote && (
                <div style={{
                    padding: '10px 14px', borderRadius: 8,
                    background: '#fffbeb', border: '1px solid #fde68a',
                    fontSize: 12, color: '#92400e',
                }}>
                    ℹ️ {revenueNote}
                </div>
            )}
        </div>
    );
};


// ─── Funnel Display ─────────────────────────────────────────────────────────

const FunnelDisplay = ({ title, data, color }) => {
    if (!data || Object.keys(data).length === 0) return null;

    // data might be an object with step names as keys, or an array
    const steps = Array.isArray(data) ? data : Object.entries(data).map(([key, value]) => ({
        step: key,
        count: typeof value === 'number' ? value : value?.count ?? 0,
        label: key.replace(/_/g, ' '),
    }));

    if (steps.length === 0) return null;

    const maxCount = Math.max(...steps.map(s => s.count || 0), 1);

    return (
        <div style={{ marginBottom: 28 }}>
            <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                marginBottom: 12, paddingBottom: 8,
                borderBottom: `2px solid ${color}20`,
            }}>
                <Target size={15} style={{ color }} />
                <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: 0 }}>{title}</h3>
            </div>
            <div style={{
                background: '#fff', border: '1px solid #e5e7eb',
                borderRadius: 14, padding: '16px 20px',
            }}>
                {steps.map((step, idx) => {
                    const count = step.count || 0;
                    const barWidth = maxCount > 0 ? Math.max((count / maxCount) * 100, count > 0 ? 3 : 0) : 0;
                    return (
                        <div key={step.step || idx} style={{ marginBottom: idx < steps.length - 1 ? 10 : 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
                                <span style={{ fontSize: 12, color: '#374151', fontWeight: 500 }}>
                                    {step.label || step.step}
                                </span>
                                <span style={{ fontSize: 13, fontWeight: 700, color: count > 0 ? '#111827' : '#d1d5db' }}>
                                    {count}
                                </span>
                            </div>
                            <div style={{ height: 5, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                                <div style={{
                                    height: '100%', width: `${barWidth}%`,
                                    background: color, borderRadius: 3,
                                    transition: 'width 0.4s', opacity: 0.7,
                                }} />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};


export default MonetizationTabV2;
